'use client';

import { useState } from 'react';
import { ContactUsBlock as IContactUsBlock } from '../types';
import { SupportedLanguages } from '@/types/globals';

export default function ContactUsBlock({
  block,
  locale,
}: {
  block: IContactUsBlock;
  locale: SupportedLanguages;
}) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: '',
    legal_consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const textColours = {
    white: { text: 'text-white', star_colour: 'text-white' },
    gray: { text: 'text-gray-900', star_colour: 'text-red-500' },
    purple: { text: 'text-qf-purple', star_colour: 'text-red-500' },
    black: { text: 'text-qf-black', star_colour: 'text-red-500' },
  };

  const PRIVACY_POLICY_URL = 'https://www.quickfactsinc.com/privacy';

  const text = {
    fr: {
      successMessage: 'Merci pour votre message!',
      thankYou: 'Merci!',
      errorMessage: 'Une erreur est survenue. Veuillez réessayer.',
      firstNamePlaceholder: 'Prénom',
      lastNamePlaceholder: 'Nom',
      emailPlaceholder: 'Email',
      companyNamePlaceholder: 'Nom de la société',
      messagePlaceholder: 'Message',
      sending: "En cours d'envoi...",
      sendMessage: 'Envoyer Message',
      legalConsentBefore: 'Vous acceptez notre ',
      legalConsentLink: 'politique de confidentialité',
      legalConsentAfter: '.',
    },
    en: {
      successMessage: 'Thank you for your message!',
      thankYou: 'Thank You!',
      errorMessage: 'Something went wrong. Please try again.',
      firstNamePlaceholder: 'First Name',
      lastNamePlaceholder: 'Last Name',
      emailPlaceholder: 'Email',
      companyNamePlaceholder: 'Company Name',
      messagePlaceholder: 'Message',
      sending: 'Sending...',
      sendMessage: 'Send Message',
      legalConsentBefore: 'You agree to our friendly ',
      legalConsentLink: 'privacy policy',
      legalConsentAfter: '.',
    },
  };

  const handleInputChange = (
    element: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = element.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const submitToHubSpot = async (formData: {
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    message: string;
    legal_consent: boolean;
  }) => {
    const hubspotPortalId = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID;
    const hubspotFormId = process.env.NEXT_PUBLIC_CONTACT_FORM_ID;

    if (!hubspotPortalId || !hubspotFormId) {
      throw new Error('HubSpot configuration missing');
    }

    const payload = {
      fields: [
        {
          name: 'firstname',
          value: formData.firstName,
        },
        {
          name: 'lastname',
          value: formData.lastName,
        },
        {
          name: 'email',
          value: formData.email,
        },
        {
          name: 'company',
          value: formData.company,
        },
        {
          name: 'message',
          value: formData.message,
        },
        {
          name: 'legal_consent',
          value: formData.legal_consent,
        },
      ],
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotPortalId}/${hubspotFormId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Form submission failed: ${response.status} - ${errorText}`,
      );
    }

    return response.json();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await submitToHubSpot(formData);
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        message: '',
        legal_consent: false,
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(text[locale].errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const textColour = textColours[block.text_colour || 'gray'];

  if (isSubmitted) {
    return (
      <section className="relative overflow-hidden px-4 py-20">
        <div className="relative mx-auto max-w-6xl text-center">
          <div className="mx-auto max-w-2xl">
            <h2
              className="mb-6 text-3xl font-bold md:text-4xl"
              dangerouslySetInnerHTML={{ __html: text[locale].thankYou }}
            />
            <p
              className="text-lg opacity-90 md:text-xl"
              dangerouslySetInnerHTML={{ __html: text[locale].successMessage }}
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden px-4 py-20">
      <div className="relative mx-auto max-w-6xl text-center">
        {/* Newsletter label */}
        <div className="mb-8">
          <span
            className={`text-sm font-semibold tracking-wider uppercase ${textColour.text}`}
          >
            <span dangerouslySetInnerHTML={{ __html: block.pre_title || '' }} />
          </span>
        </div>

        {/* Main heading */}
        <div className="mx-auto mb-8 max-w-2xl">
          <h2
            className={`mb-6 text-3xl font-bold md:text-4xl ${textColour.text}`}
            dangerouslySetInnerHTML={{ __html: block.title || '' }}
          />
          <p
            className={`text-lg md:text-xl ${textColour.text}`}
            dangerouslySetInnerHTML={{ __html: block.description || '' }}
          />
        </div>

        {/* Custom Form */}
        <form onSubmit={handleSubmit} className="mx-auto max-w-4xl">
          <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-end">
            {/* First Name Input */}
            <div className="w-full md:flex-1">
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder={text[locale].firstNamePlaceholder}
                required
                className="w-full rounded-lg bg-white px-6 py-4 text-gray-900 placeholder-gray-600 transition-all"
              />
            </div>

            {/* Last Name Input */}
            <div className="w-full md:flex-1">
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder={text[locale].lastNamePlaceholder}
                required
                className="w-full rounded-lg bg-white px-6 py-4 text-gray-900 placeholder-gray-600 transition-all"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col items-stretch gap-4 md:flex-row md:items-end">
            {/* Email Input */}
            <div className="w-full md:flex-1">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={text[locale].emailPlaceholder}
                required
                className="w-full rounded-lg bg-white px-6 py-4 text-gray-900 placeholder-gray-600 transition-all"
              />
            </div>

            {/* Company Name Input */}
            <div className="w-full md:flex-1">
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder={text[locale].companyNamePlaceholder}
                required
                className="w-full rounded-lg bg-white px-6 py-4 text-gray-900 placeholder-gray-600 transition-all"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col items-stretch gap-4 md:flex-row md:items-end">
            {/* Message Input */}
            <div className="w-full md:flex-1">
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={text[locale].messagePlaceholder}
                required
                className="h-30 w-full rounded-lg bg-white px-6 py-4 text-gray-900 placeholder-gray-600 transition-all"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col items-center justify-center gap-4 md:flex-row">
            <div>
              <input
                type="checkbox"
                name="legal_consent"
                value={formData.legal_consent ? 'true' : 'false'}
                onChange={handleInputChange}
                required
              />
              <label
                htmlFor="legal_consent"
                className={`text-md pl-2 ${textColour.text}`}
              >
                {text[locale].legalConsentBefore}
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold hover:underline"
                  aria-label="Privacy Policy"
                >
                  {text[locale].legalConsentLink}
                </a>
                {text[locale].legalConsentAfter}
              </label>
              <span className={`text-lg ${textColour.star_colour}`}>*</span>
            </div>
          </div>
          <div className="mt-4 flex flex-col items-center gap-4 md:flex-row">
            {/* Submit Button */}
            <div className="flex flex-1 justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="text-qf-purple flex items-center gap-2 rounded-lg bg-white px-8 py-4 font-semibold transition-all"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-teal-600 border-t-transparent"></div>
                    {text[locale].sending}
                  </>
                ) : (
                  <>{text[locale].sendMessage}</>
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-opacity-20 mt-4 rounded-lg border border-red-300 bg-red-500 p-3 text-sm text-red-100">
              {error}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
